/**
 * Q32.32 signed fixed-point number with Int64 semantics, on BigInt (ADR 0002).
 * Multiplication and division truncate toward zero on the exact result; parse and
 * fromFraction round half to even; anything outside Int64 throws, as the C# reference does.
 */
const FRACTIONAL_BITS = 32n;
const ONE_RAW = 1n << FRACTIONAL_BITS;
const MAX_RAW = (1n << 63n) - 1n;
const MIN_RAW = -(1n << 63n);
const FRACTION_MASK = ONE_RAW - 1n;

function checkRange(raw: bigint, what: string): bigint {
  if (raw > MAX_RAW || raw < MIN_RAW) {
    throw new RangeError(`Fix64 overflow in ${what}`);
  }
  return raw;
}

function roundHalfEven(quotient: bigint, remainder: bigint, divisor: bigint): bigint {
  const twice = remainder * 2n;
  if (twice > divisor) return quotient + 1n;
  if (twice === divisor && (quotient & 1n) === 1n) return quotient + 1n;
  return quotient;
}

export class Fix64 {
  static readonly FractionalBits = 32;
  static readonly OneRaw = ONE_RAW;

  private constructor(readonly raw: bigint) {}

  static readonly Zero = new Fix64(0n);
  static readonly One = new Fix64(ONE_RAW);
  static readonly Two = new Fix64(ONE_RAW * 2n);
  static readonly Half = new Fix64(ONE_RAW / 2n);
  static readonly MaxValue = new Fix64(MAX_RAW);
  static readonly MinValue = new Fix64(MIN_RAW);

  static fromRaw(raw: bigint): Fix64 {
    return new Fix64(checkRange(raw, "fromRaw"));
  }

  static fromInt(value: number): Fix64 {
    if (!Number.isInteger(value) || value > 2147483647 || value < -2147483648) {
      throw new RangeError("Fix64.fromInt: integer part out of range");
    }
    return new Fix64(BigInt(value) << FRACTIONAL_BITS);
  }

  /** Exact rational numerator/denominator rounded half to even. */
  static fromFraction(numerator: bigint | number, denominator: bigint | number): Fix64 {
    let n = BigInt(numerator);
    let d = BigInt(denominator);
    if (d === 0n) throw new RangeError("Fix64.fromFraction: divide by zero");
    const negative = n < 0n !== d < 0n;
    if (n < 0n) n = -n;
    if (d < 0n) d = -d;
    const scaled = n << FRACTIONAL_BITS;
    const q = roundHalfEven(scaled / d, scaled % d, d);
    return new Fix64(checkRange(negative ? -q : q, "fromFraction"));
  }

  /** Parses "-12.375" exactly (at most 9 fractional digits, no exponent), rounding half to even. */
  static parse(text: string): Fix64 {
    const v = Fix64.tryParse(text);
    if (v === null) throw new SyntaxError(`Fix64.parse: invalid decimal '${text}'`);
    return v;
  }

  static tryParse(text: string): Fix64 | null {
    const m = /^([+-]?)(\d+)(?:\.(\d{1,9}))?$/.exec(text);
    if (!m) return null;
    const negative = m[1] === "-";
    const intPart = BigInt(m[2]);
    if (intPart > 2147483647n) return null;
    let magnitude = intPart << FRACTIONAL_BITS;
    if (m[3] !== undefined) {
      const frac = BigInt(m[3]);
      const pow10 = 10n ** BigInt(m[3].length);
      const scaled = frac << FRACTIONAL_BITS;
      magnitude += roundHalfEven(scaled / pow10, scaled % pow10, pow10);
    }
    return new Fix64(checkRange(negative ? -magnitude : magnitude, "parse"));
  }

  add(b: Fix64): Fix64 {
    return new Fix64(checkRange(this.raw + b.raw, "add"));
  }

  sub(b: Fix64): Fix64 {
    return new Fix64(checkRange(this.raw - b.raw, "sub"));
  }

  neg(): Fix64 {
    return new Fix64(checkRange(-this.raw, "neg"));
  }

  /** Truncates toward zero (BigInt division semantics), matching the C# reference. */
  mul(b: Fix64): Fix64 {
    return new Fix64(checkRange((this.raw * b.raw) / ONE_RAW, "mul"));
  }

  mulInt(b: number): Fix64 {
    return new Fix64(checkRange(this.raw * BigInt(b), "mulInt"));
  }

  div(b: Fix64): Fix64 {
    if (b.raw === 0n) throw new RangeError("Fix64 divide by zero");
    return new Fix64(checkRange((this.raw << FRACTIONAL_BITS) / b.raw, "div"));
  }

  divInt(b: number): Fix64 {
    if (b === 0) throw new RangeError("Fix64 divide by zero");
    return new Fix64(this.raw / BigInt(b));
  }

  eq(b: Fix64): boolean { return this.raw === b.raw; }
  lt(b: Fix64): boolean { return this.raw < b.raw; }
  le(b: Fix64): boolean { return this.raw <= b.raw; }
  gt(b: Fix64): boolean { return this.raw > b.raw; }
  ge(b: Fix64): boolean { return this.raw >= b.raw; }
  isZero(): boolean { return this.raw === 0n; }
  isPositive(): boolean { return this.raw > 0n; }
  isNegative(): boolean { return this.raw < 0n; }

  cmp(b: Fix64): number {
    return this.raw < b.raw ? -1 : this.raw > b.raw ? 1 : 0;
  }

  abs(): Fix64 { return this.raw < 0n ? this.neg() : this; }
  sign(): number { return this.raw < 0n ? -1 : this.raw > 0n ? 1 : 0; }
  sqr(): Fix64 { return this.mul(this); }

  floor(): Fix64 { return new Fix64(this.raw & ~FRACTION_MASK); }

  ceil(): Fix64 {
    const f = this.raw & ~FRACTION_MASK;
    return f === this.raw ? this : new Fix64(checkRange(f + ONE_RAW, "ceil"));
  }

  /** Floor to a JS integer (arithmetic shift). */
  floorToInt(): number { return Number(this.raw >> FRACTIONAL_BITS); }
  ceilToInt(): number { return this.ceil().floorToInt(); }
  frac(): Fix64 { return new Fix64(this.raw & FRACTION_MASK); }

  static min(a: Fix64, b: Fix64): Fix64 { return a.raw <= b.raw ? a : b; }
  static max(a: Fix64, b: Fix64): Fix64 { return a.raw >= b.raw ? a : b; }
  static clamp(v: Fix64, lo: Fix64, hi: Fix64): Fix64 { return v.raw < lo.raw ? lo : v.raw > hi.raw ? hi : v; }

  /** Decimal rendering truncated to `digits` places. Debug/UI only. */
  toDecimalString(digits = 6): string {
    const negative = this.raw < 0n;
    const magnitude = negative ? -this.raw : this.raw;
    const intPart = magnitude >> FRACTIONAL_BITS;
    let frac = magnitude & FRACTION_MASK;
    let s = (negative ? "-" : "") + intPart.toString();
    if (digits > 0) {
      s += ".";
      for (let i = 0; i < digits; i++) {
        frac *= 10n;
        s += (frac >> FRACTIONAL_BITS).toString();
        frac &= FRACTION_MASK;
      }
    }
    return s;
  }

  toString(): string { return this.toDecimalString(6); }

  /** Lossy conversion for rendering and logging only; never use in simulation code. */
  toNumber(): number { return Number(this.raw) / 4294967296; }
}
